import React, {useEffect, useState} from 'react';
import {Box} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import InternalTemplate from '../../../../../components/templates/internal';
import {IHistoryDetail} from '../../../../../interfaces/dtos';
import {StackParamList} from '../../../../../navigators';
import {HistoryItem} from './item';
import {SpinnerModal} from '../../../../../components/modals';
import HeaderBox from './header-box';
import {useHistoryList} from './hooks/history-list.hook';
import {EmptyListIcon} from '../../../../../components/icons';
import {isOdd} from '../../../../../utils/ui';
import {useMainProperties} from '../../../../../hooks/app/useMainProperties';

type ScreenProps = NativeStackScreenProps<StackParamList, 'HistoryList'>;

type HistoryListContentType = {
  isLoading: boolean;
  historyList: any[] | null;
  filtered: any[];
  onSelectedItem: (index: number) => void;
};

const HistoryListContent = ({
  isLoading,
  historyList,
  filtered,
  onSelectedItem,
}: HistoryListContentType) => {
  if (isLoading) {
    return <></>;
  }
  if (historyList?.length === 0) {
    return <EmptyListIcon marginTop={0} />;
  }
  if (filtered) {
    return (
      <>
        {filtered.map((detail: IHistoryDetail, index: number) => (
          <HistoryItem
            key={`balance-item-${index}`}
            id={detail.id}
            status={detail.status}
            amount={detail.amount}
            onPress={() => onSelectedItem(index)}
            rowType={isOdd(index) ? 'odd' : 'even'}
            bank={detail.bank}
            account={detail.account}
            reference={detail.reference}
            dateAt={detail.dateAt}
            comments={detail.comments}
          />
        ))}
      </>
    );
  }

  return <></>;
};

const HistoryListScreen = ({navigation}: ScreenProps) => {
  const {isSalesMan} = useMainProperties();
  const [filtered, setFiltered] = useState([] as IHistoryDetail[]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {historyList, fetchHistoryList} = useHistoryList();

  const getList = async () => {
    setIsLoading(true);
    setFiltered([]);
    await fetchHistoryList();
    setIsLoading(false);
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFiltered(historyList ?? []);
  }, [historyList, setFiltered]);

  const handleGoBack = () => {
    navigation.pop(1);
    if (isSalesMan()) {
      navigation.popTo('SalesmanClientDashboard');
    } else {
      navigation.popToTop();
    }
  };

  const handleSelectItem = (index: number) => {
    navigation.navigate('HistoryDetail', filtered[index]);
  };

  const getId = (detail: IHistoryDetail) => {
    return detail.id ? detail.id.toString() : '';
  };

  const getBankLabel = (detail: IHistoryDetail) => {
    if (detail.bank && detail.account) {
      return `${detail.bank}-${detail.account.substring(
        detail.account.length - 4,
      )}`.toUpperCase();
    }
    return '';
  };

  const handleSearchChange = (value: string) => {
    const search = value.trim().toLowerCase();
    setSearchText(search);

    if (search === '') {
      if (historyList) {
        setFiltered(historyList);
      }
      return;
    }

    if (historyList) {
      const result = historyList.filter(detail => {
        if (getId(detail).indexOf(search) > -1) {
          return true;
        }
        if (getBankLabel(detail).indexOf(search.toUpperCase()) > -1) {
          return true;
        }
      });
      setFiltered(result);
    }
  };

  return (
    <InternalTemplate
      title={'screen-history-list-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} />
      <Box paddingX={5}>
        <HeaderBox
          onChangeSearch={handleSearchChange}
          count={filtered.length}
          searchText={searchText}
        />
      </Box>
      <Box marginBottom={20}>
        <HistoryListContent
          {...{
            isLoading,
            historyList,
            filtered,
            onSelectedItem: handleSelectItem,
          }}
        />
      </Box>
    </InternalTemplate>
  );
};
export default HistoryListScreen;
